import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

/**
 * List state that lives in the URL (`plan-beta` E, and B5's carry-forward).
 *
 * ### What was wrong
 *
 * Every list held its filters in `useState`, so a view could not be sent to
 * anyone. "The certified behavioural items with no Arabic prompt" was a set of
 * clicks to describe rather than a link to paste, the back button silently
 * discarded a search, and a refresh dropped an operator on page 1 of everything.
 * `plan-beta` B5 asks for it as *"make the URL carry filter state so a search is
 * shareable"* and E repeats it; they are one mechanism, which is why T3 left it
 * here rather than building half of it twice.
 *
 * ### Two rules that make a URL readable rather than a state dump
 *
 * **1. Only non-default values are written.** The default view of a list is
 * `/questions`, not `/questions?module=__all__&type=__all__&status=__all__&page=1`.
 * A URL that restates the defaults is one nobody reads and every screen produces.
 *
 * **2. Changing a filter resets the page.** Otherwise narrowing a search while on
 * page 7 lands on page 7 of two pages — an empty list that looks like "no
 * results" and is really "no such page". `page` is the one key this hook knows
 * the meaning of, and this is why.
 *
 * ### What is deliberately NOT in the URL
 *
 * **The selection.** `plan-beta` E lists it, and it is the one item there worth
 * refusing: a link carrying 40 item ids is unreadable, breaks at a few hundred,
 * and — more to the point — sharing a *view* is useful where sharing a *pending
 * bulk action* is not. Selection stays in memory and is cleared by navigation,
 * which is the honest behaviour anyway.
 */
export type UrlState = Record<string, string>;

export function useUrlState<T extends UrlState>(
  defaults: T,
): [T, (patch: Partial<T>) => void, () => void] {
  const [params, setParams] = useSearchParams();

  const state = useMemo(() => {
    const next = { ...defaults };
    for (const key of Object.keys(defaults)) {
      const value = params.get(key);
      if (value !== null) next[key as keyof T] = value as T[keyof T];
    }
    return next;
  }, [params, defaults]);

  const set = useCallback(
    (patch: Partial<T>) => {
      const next = new URLSearchParams(params);

      // Rule 2, before the patch is applied — a caller that changes a filter AND
      // the page in one call means the page it asked for.
      const changesAFilter = Object.keys(patch).some((k) => k !== 'page');
      if (changesAFilter && !('page' in patch)) next.delete('page');

      for (const [key, value] of Object.entries(patch)) {
        // Rule 1: a value equal to the default is an absent parameter.
        if (value === undefined || value === defaults[key]) next.delete(key);
        else next.set(key, String(value));
      }

      // `replace`, so filtering does not fill the back button with every
      // keystroke of a search box. The shareable URL is still current.
      //
      // Since the react-router 7 upgrade this navigation happens inside a
      // `React.startTransition` (v6's `v7_startTransition`, on by default in 7),
      // so the new URL is NOT readable on the line after the call — a caller
      // that needs it must wait for a render. Both rules above are unchanged by
      // that, and `test/url-state.test.tsx` is what says so.
      setParams(next, { replace: true });
    },
    [params, setParams, defaults],
  );

  const clear = useCallback(() => setParams(new URLSearchParams(), { replace: true }), [setParams]);

  return [state, set, clear];
}

/* ------------------------------------------------------------ saved filters */

const SAVED_KEY = 'nafis-saved-filters';

export interface SavedFilter {
  name: string;
  /** The query string, without the leading `?`. */
  query: string;
  /** Which list it belongs to, so Questions does not offer a Candidates filter. */
  path: string;
}

/**
 * Named filters, kept in this browser.
 *
 * **Device-local on purpose, where the display language is on the account.** The
 * two look similar and are not: a language is how a person reads the console
 * everywhere, and a saved filter is a shortcut to a view whose shareable form is
 * the URL itself — which this workstream just made work. Putting shortcuts on
 * the account would need a route, a migration and an opinion about whose they
 * are; putting them here costs nothing and loses nothing that the URL does not
 * already carry.
 */
export function readSavedFilters(): SavedFilter[] {
  try {
    const raw = localStorage.getItem(SAVED_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as SavedFilter[]) : [];
  } catch {
    // A corrupt entry means "no saved filters", never a broken screen.
    return [];
  }
}

export function writeSavedFilters(filters: SavedFilter[]): void {
  try {
    localStorage.setItem(SAVED_KEY, JSON.stringify(filters));
  } catch {
    // Storage disabled (private mode). Saving simply does not persist.
  }
}
